import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@component/dialog";
import { Badge } from "@component/badge";
import { Button } from "@component/button";
import { Textarea } from "@component/textarea";
import {
  BarChart3Icon,
  AlertTriangleIcon,
  FileTextIcon,
  ClipboardCheckIcon,
  XIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CornerDownRightIcon,
} from "lucide-react";
import ScoreSectionCollapsible from "@component/score-section-collapsible";
import type { ScoreBreakdownItem } from "@component/score-section-collapsible";
import { getGenerateScoreExplanation, getScoreHistory } from "@service/job";
import { getInterviewInsights } from "@service/interview";
import { getUserInterviewSubmission } from "@service/assessment";
import { InterviewInsight } from "@type/videoInterview";
import type { UserInterview, UserSubmissionJob } from "@type/assessments";
import { getUserSubmissionJob } from "@service/cv";
import { parseFormat } from "@util/date";
import { submitReportMatchScoreBugs } from "@service/report";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import { Applicant } from "@type/applicant";
import { convertToLowerSnakeCase, getApplicantStatusBadge, getFunnelStageByStatus } from "@util/string";
import { availabilityOptions, getEmploymentStatusLabel, getWorkAuthorizationLabel } from "@constant/app";
import { Popover, PopoverContent, PopoverTrigger } from "@component/popover";
import { cn } from "@util/tailwind";
import { Tooltip } from "@component/tooltip";

interface ScoreBreakdownDialogProps {
  isOpen: boolean;
  onClose: () => void;
  applicant: Applicant
  candidate_id: string
  candidate_name: string;
  match_score: number;
  job_id?: string
  job_title?: string;
  applied_date?: string;
  assessment_ids?: { type: string; id: string; name: string }[];
}

export default function ScoreBreakdownDialog({
  isOpen,
  onClose,
  applicant,
  candidate_id,
  candidate_name,
  match_score,
  job_id,
  job_title,
  applied_date,
  assessment_ids,
}: ScoreBreakdownDialogProps) {
  const snackbar = useSnackbar();
  const [showScorePopover, setShowScorePopover] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportComment, setReportComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isLoadingGenerateExplanation, setIsLoadingGenerateExplanation] = useState<boolean>(false);
  // The overall/CV/assessment scores come from getScoreHistory — gate the header
  // badge on this so it shows a loader (not a premature CV-only value) until it lands.
  const [isLoadingScoreHistory, setIsLoadingScoreHistory] = useState<boolean>(false);
  const [selectedMustHaves, setSelectedMustHaves] = useState<{ label: string; matched: boolean; explanation?: string }[]>([]);
  const [showMustHaves, setShowMustHaves] = useState(false);
  const [interviewInsights, setInterviewInsights] = useState<InterviewInsight | null>(null);
  const [isLoadingInterviewInsight, setIsLoadingInterviewInsight] = useState<boolean>(false);
  const [formDisplay, setFormDisplay] = useState<UserSubmissionJob | null>(null);
  const [stageScores, setStageScores] = useState<{ total_score: number; category_scores: any; trigger: string; recorded_at: string }[]>([]);
  const [assessmentScoreFromHistory, setAssessmentScoreFromHistory] = useState<number | null>(null);
  const [cvScoreFromHistory, setCvScoreFromHistory] = useState<number | null>(null);
  const [formScoreFromHistory, setFormScoreFromHistory] = useState<number | null>(null);
  const [breakdownItems, setBreakdownItems] = useState<
    {
      category: string;
      score: number;
      max_score: number;
      remarks: string;
      tags: string[];
      mustHaves?: { label: string; matched: boolean; explanation?: string }[];
    }[]
  >([]);
  const [max_score, setMaxScore] = useState<number>(0);
  const [totalScore, setTotalScore] = useState<number>(0);
  const [scoreExplanationMetadata, setScoreExplanationMetadata] = useState<{
    is_ai_assessment_completed: boolean;
    is_form_assessment_completed: boolean;
    assessment_status: string;
    assessment_invited_at: string | null;
    assessment_started_at: string | null;
    assessment_completed_at: string | null;
  } | null>(null);

  useEffect(() => {
    if (isOpen) {
      setIsLoadingGenerateExplanation(true);
      setIsLoadingScoreHistory(true);
      setIsLoadingInterviewInsight(true);
      setInterviewInsights(null);
      setFormDisplay(null);
      setStageScores([]);
      setAssessmentScoreFromHistory(null);
      setCvScoreFromHistory(null);
      setFormScoreFromHistory(null);

      // Fetch score history
      if (job_id && candidate_id) {
        getScoreHistory(candidate_id, job_id).then((resp) => {
          if (resp.status === 200 && resp.data.data) {
            const data = resp.data.data;
            console.log('data', data.current_score);
            if (data.history) {
              setStageScores(data.history);
            }
            if (data.current_score?.interview_score) {
              setAssessmentScoreFromHistory(data.current_score.interview_score);
            }
            if (data.current_score?.cv_score) {
              setCvScoreFromHistory(data.current_score.cv_score);
            }
            if (data.current_score?.form_score) {
              setFormScoreFromHistory(data.current_score.form_score);
            }
          }
        }).catch((err) => {
          console.error("Failed to get score history", err);
        }).finally(() => {
          setIsLoadingScoreHistory(false);
        });
      } else {
        setIsLoadingScoreHistory(false);
      }

      // Fetch form submission data
      if (job_id && candidate_id) {
        getUserSubmissionJob(job_id, candidate_id).then((resp) => {
          if (resp.status === 200) {
            setFormDisplay(resp.data.data);
          }
        }).catch((err) => {
          console.error("Failed to get form submission", err);
        });
      }

      getGenerateScoreExplanation(job_id || "", candidate_id).then((resp) => {
        if (resp.status === 200) {
          const mustHaveHits = (resp.data.data.must_have_hits || []).map(
            (hit: { must_have_criteria: string; is_hit: boolean; explanation: string }) => ({
              label: hit.must_have_criteria,
              matched: hit.is_hit,
              explanation: hit.explanation,
            })
          );
          setBreakdownItems(
            (resp.data.data.explanation || []).map((item: any) => ({
              ...item,
              mustHaves: item.category === "Must-haves" ? mustHaveHits : undefined,
            }))
          );
          setMaxScore(resp.data.data.max_total_score);
          setTotalScore(resp.data.data.total_score);
          const metadata = {
            is_ai_assessment_completed: resp.data.data.is_ai_assessment_completed,
            is_form_assessment_completed: resp.data.data.is_form_assessment_completed,
            assessment_status: resp.data.data.assessment_status,
            assessment_invited_at: resp.data.data.assessment_invited_at,
            assessment_started_at: resp.data.data.assessment_started_at,
            assessment_completed_at: resp.data.data.assessment_completed_at,
          };
          setScoreExplanationMetadata(metadata);

          // Fetch interview insights if AI assessment is completed
          if (metadata.is_ai_assessment_completed) {
            console.log('assessment_ids', assessment_ids)
            const interviewAssessments = assessment_ids?.filter(a => a.type === "Interview") || [];

            if (interviewAssessments.length > 0 && candidate_id) {
              // Job has interview assessment — try to get job-specific submission
              getUserInterviewSubmission(interviewAssessments[0].id, candidate_id)
                .then((submission: UserInterview) => {
                  if (submission && submission.conversation_id) {
                    // Found submission, get insights using conversation_id
                    return getInterviewInsights(submission.conversation_id, undefined, candidate_id);
                  }
                  // No submission found, fallback to general interview
                  return getInterviewInsights(undefined, "First Time Interview", candidate_id);
                })
                .then((res) => {
                  if (res?.insights) {
                    setInterviewInsights(JSON.parse(res.insights));
                  }
                })
                .catch((error) => {
                  console.error("Failed to get interview insights", error);
                  // Fallback to general interview on error
                  getInterviewInsights(undefined, "First Time Interview", candidate_id)
                    .then((res) => {
                      if (res?.insights) {
                        setInterviewInsights(JSON.parse(res.insights));
                      }
                    })
                    .catch((err) => {
                      console.error("Failed to get general interview insights", err);
                    });
                })
                .finally(() => {
                  setIsLoadingInterviewInsight(false);
                });
            } else {
              // No interview assessment on job, use general interview
              getInterviewInsights(undefined, "First Time Interview", candidate_id)
                .then((res) => {
                  if (res?.insights) {
                    setInterviewInsights(JSON.parse(res.insights));
                  }
                })
                .catch((err) => {
                  console.error("Failed to get general interview insights", err);
                })
                .finally(() => {
                  setIsLoadingInterviewInsight(false);
                });
            }
          } else {
            setIsLoadingInterviewInsight(false);
          }
        }
      }).catch((err) => {
        catchErrorLog(`Error generating score explanation: ${err}`);
        setIsLoadingInterviewInsight(false);
      }).finally(() => {
        setIsLoadingGenerateExplanation(false);
      });
    } else {
      return () => {
        setBreakdownItems([]);
        setReportComment("");
        setShowReportForm(false);
        setSubmitSuccess(false);
        setScoreExplanationMetadata(null);
        setInterviewInsights(null);
        setFormDisplay(null);
        setStageScores([]);
      }
    }
  }, [isOpen])

  const getMatchBadgeColor = (percentage: number) => {
    if (percentage >= 80) {
      return "bg-green-100 hover:bg-green-200 text-green-800 dark:bg-green-900/30 dark:text-green-300";
    }
    if (percentage >= 60) {
      return "bg-yellow-100 hover:bg-yellow-200 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
    }
    return "bg-red-100 hover:bg-red-200 text-red-800 dark:bg-red-900/30 dark:text-red-300";
  };

  const handleSubmitReport = () => {
    setIsSubmitting(true);
    submitReportMatchScoreBugs({
      bug_type: "scoring_feedback",
      bug_description: reportComment,
      meta: {
        candidate_id,
        candidate_name,
        job_id,
        job_title,
      },
    }).then((resp) => {
      if (resp.status === 201) {
        onClose();
        snackbar.showSuccess("Report submitted successfully.");
        setSubmitSuccess(true);

      }
    }).catch((err) => {
      onClose();
      catchErrorLog(`Error submitting score breakdown report: ${err}`);
      snackbar.showError("Failed to submit report. Please try again later.");
    }).finally(() => {
      setIsSubmitting(false);
    });
  };

  const handleCancelReport = () => {
    setShowReportForm(false);
    setReportComment("");
    setSubmitSuccess(false);
  };

  const getTagStyle = (tag: string) => {
    const tagStyles: Record<string, string> = {
      'Top Tier': 'bg-orange-50 text-orange-600',
      'High Impact': 'bg-purple-50 text-purple-600',
      'Consistent Performer': 'bg-purple-50 text-purple-600',
      'Technical Expertise': 'bg-blue-50 text-blue-600',
      'Creative Excellence': 'bg-pink-50 text-pink-600',
      'Global Exposure': 'bg-cyan-50 text-cyan-600',
      'Emerging Talent': 'bg-orange-50 text-orange-600',
      'AI Literate': 'bg-purple-50 text-purple-600',
      'Award Winning': 'bg-orange-50 text-orange-600',
      'Academic Excellence': 'bg-green-50 text-green-600',
    };
    return tagStyles[tag] || 'bg-gray-50 text-gray-600';
  };

  // CV score from score history
  const cvScore = Math.round(cvScoreFromHistory ?? (stageScores.length > 0 ? stageScores[0].total_score : match_score));
  const assessmentScore = (() => {
    const hasAI = assessmentScoreFromHistory !== null;
    const hasForm = formScoreFromHistory !== null;
    if (hasAI && hasForm) return Math.round(assessmentScoreFromHistory * 0.7 + formScoreFromHistory * 0.3);
    if (hasAI) return Math.round(assessmentScoreFromHistory);
    if (hasForm) return Math.round(formScoreFromHistory);
    return null;
  })();

  // Whether each assessment part exists — driven by the authoritative
  // generate-score-explanation flags once loaded, else inferred from score
  // history (which lands faster) so the Assessment Score section + its scores
  // can render immediately instead of waiting on the explanation API. Only the
  // pieces that genuinely need that API (the AI Interview breakdown rows, the
  // completed date) load in place.
  const hasAiAssessment = scoreExplanationMetadata?.is_ai_assessment_completed ?? (assessmentScoreFromHistory !== null);
  const hasFormAssessment = scoreExplanationMetadata?.is_form_assessment_completed ?? (formScoreFromHistory !== null);
  const showAssessmentSection = hasAiAssessment || hasFormAssessment;

  // Total score = (cvScore * 0.7) + (assessmentScore * 0.3)
  const totalScoreFromHistory = assessmentScore !== null
    ? Math.round(cvScore * 0.7 + assessmentScore * 0.3)
    : cvScore;

  // Score movement: before = cvScore (before assessment), after = totalScoreFromHistory
  const previousScore = cvScore;
  const overallScoreDiff = assessmentScore !== null ? totalScoreFromHistory - previousScore : null;
  const scoreDiff = overallScoreDiff;

  // Assessment Details (status + timeline) don't need generate-score-explanation:
  // the applicant prop already carries the invite/complete timestamps and status.
  // Prefer the explanation metadata once it lands (authoritative), else fall back
  // to the applicant so the card renders immediately instead of blank. `started_at`
  // has no clean applicant equivalent, so it stays metadata-only (fills in later).
  const assessmentInvitedAt = scoreExplanationMetadata ? scoreExplanationMetadata.assessment_invited_at : (applicant.invitation_date || null);
  const assessmentStartedAt = scoreExplanationMetadata ? scoreExplanationMetadata.assessment_started_at : null;
  const assessmentCompletedAt = scoreExplanationMetadata ? scoreExplanationMetadata.assessment_completed_at : (applicant.completed_at || null);
  const assessmentStatusRaw = scoreExplanationMetadata
    ? scoreExplanationMetadata.assessment_status
    : (applicant.completed_at ? "Complete" : applicant.aborted_at ? "Aborted" : applicant.partial_at ? "Partial" : "");

  const status: string = (["Pending Assessment", "Upload Complete", "CV Parsed"].includes(applicant.status) && !assessmentInvitedAt) ? "Pending Invite" :
    (["Partial", "Aborted"].includes(assessmentStatusRaw || "") && applicant.status === "Pending Assessment") ? "In Progress" :
      (applicant.status === "Pending Assessment" && assessmentInvitedAt) ? "Awaiting Completion" :
        (assessmentStatusRaw === "Complete") ? "Completed" : applicant.status

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn("max-h-[90vh] overflow-hidden flex flex-row transition-all duration-300", showMustHaves ? "max-w-[1400px]" : "max-w-6xl")}
        closeButtonClassName={showMustHaves ? "hidden" : "top-7"}
      >
        <div className="flex-1 flex flex-col overflow-y-auto min-w-0">
        <DialogHeader className="relative">
          <DialogTitle className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-3">
            <BarChart3Icon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            Match Score Breakdown - {candidate_name}

            <Popover open={showScorePopover} onOpenChange={setShowScorePopover}>
              <PopoverTrigger asChild>
                <div className="cursor-pointer">
                  <Tooltip content="View score movement" side="bottom">
                      <div className="flex flex-row gap-2">
                        <Badge
                        className={`${isLoadingScoreHistory ? "bg-gray-100 dark:bg-gray-800" : getMatchBadgeColor(Math.round(totalScoreFromHistory))} text-lg px-4 py-1.5 font-semibold cursor-pointer hover:opacity-80 hover:shadow-md transition-all duration-200`}
                      style={{
                        border: "0.909091px solid rgba(32, 23, 23, 0)",
                        borderRadius: "1rem",
                        boxShadow: "none",
                        paddingTop: "2px",
                        paddingBottom: "2px",
                      }}
                    >
                      {/* Overall score = cvScore + assessmentScore, both from getScoreHistory
                          — NOT generate-score-explanation. Show a skeleton until that history
                          lands (else the badge flashes a premature CV-only value); the
                          denominator defaults to 100 (a weighted 0-100 score) until the API's
                          max_total_score arrives. */}
                      {isLoadingScoreHistory ? (
                        <span className="inline-block h-5 w-14 rounded bg-gray-200 dark:bg-gray-700 animate-pulse align-middle" />
                      ) : (
                        <>{Math.round(totalScoreFromHistory)}/{max_score || 100}</>
                      )}
                    </Badge>
                        {scoreDiff !== null && scoreDiff !== 0 && (
                          <div
                            className={cn(
                              "flex items-center gap-0.5 text-sm font-semibold",
                              scoreDiff > 0
                                ? "text-green-600 dark:text-green-400"
                                : "text-red-600 dark:text-red-400"
                            )}
                          >
                            {scoreDiff > 0 ? (
                              <ArrowUpIcon className="w-4 h-4" />
                            ) : (
                              <ArrowDownIcon className="w-4 h-4" />
                            )}
                            <span>{parseFloat(Math.abs(scoreDiff).toFixed(2))}</span>
                          </div>
                        )}
                      </div>
                  </Tooltip>
                </div>
              </PopoverTrigger>
              <PopoverContent
                className="w-[460px] p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg"
                align="start"
                side="bottom"
                sideOffset={8}
              >
                <div className="space-y-3">
                  {/* Header */}
                    <div className="flex items-center justify-between pb-2">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Score movement
                    </h4>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowScorePopover(false);
                      }}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Score Comparison Table */}
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 pb-1">
                      <div></div>
                      <div className="text-center">BEFORE</div>
                      <div className="text-center">AFTER</div>
                    </div>
                    {/* CV Score Row */}
                    <div className="grid grid-cols-3 gap-2 items-center py-2 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        CV Score
                        <div className="text-xs text-gray-500 dark:text-gray-400">(70% weightage)</div>
                      </div>
                      <div className="text-center">
                        <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {cvScore}
                        </span>
                      </div>
                      <div className="text-center">
                        <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {cvScore}
                        </span>
                      </div>
                    </div>

                    {/* Assessment Row */}
                    <div className="grid grid-cols-3 gap-2 items-center py-2 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        Screening Score
                        <div className="text-xs text-gray-500 dark:text-gray-400">(30% weightage)</div>
                      </div>
                      <div className="text-center">
                        <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                      </div>
                      {assessmentScore !== null && (
                        <div className="text-center">
                            <span className={`text-lg text-gray-900 dark:text-gray-100 ${assessmentScore > 0 ? "font-bold" : ""}`}>
                              {assessmentScore > 0 ? assessmentScore : "—"}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 items-center py-2 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        Overall Score
                      </div>
                      <div className="text-center">
                        <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {previousScore}
                        </span>
                      </div>
                      <div className="text-center flex items-center justify-center gap-1">
                        <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                            {Math.round(totalScoreFromHistory)}
                        </span>
                        {scoreDiff !== null && scoreDiff !== 0 && (
                          <div className="flex items-center gap-0.5">
                            {scoreDiff > 0 ? (
                              <ArrowUpIcon className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                            ) : (
                              <ArrowDownIcon className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
                            )}
                            <span className={`text-xs font-semibold ${scoreDiff > 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                                {parseFloat(Math.abs(scoreDiff).toFixed(2))}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </PopoverContent>
              </Popover>
          </DialogTitle>
            {showMustHaves && (
              <button
                onClick={onClose}
                className="absolute right-4 top-0 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <XIcon className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </button>
            )}
        </DialogHeader>

        <div className="py-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Application Details Card - Combined Position & Date */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-300 dark:border-gray-600 transition-all duration-200 hover:shadow-lg hover:border-blue-500 dark:hover:border-blue-400">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide font-semibold">
                Application Details
              </div>

              <div className="flex gap-6">
                {/* Position Section */}
                <div className="flex-1">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Position
                  </div>
                  <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                    {job_title}
                  </div>
                </div>

                {/* Vertical Divider */}
                <div className="w-px bg-gray-200 dark:bg-gray-700" />

                {/* Applied Date Section */}
                <div className="flex-1">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Applied Date
                  </div>
                  <div className="text-sm text-gray-900 dark:text-gray-100 font-medium mb-1">
                    {applied_date ? parseFormat(applied_date, "D MMMM YYYY") : ""} at {applied_date ? parseFormat(applied_date, "h:mm A") : ""}
                  </div>

                  {/* CV Indicator */}
                  <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                    <svg
                      className="w-3.5 h-3.5 text-green-600 dark:text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <Tooltip content={applicant.filename || ""}>
                      <span className="truncate">
                        {applicant.filename && applicant.filename.length > 20
                          ? `${applicant.filename.slice(0, applicant.filename.length - 6).slice(0, 14)}...${applicant.filename.slice(-6)}`
                          : applicant.filename}
                      </span>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>

            {/* Assessment Details Card */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-300 dark:border-gray-600 transition-all duration-200 hover:shadow-lg hover:border-blue-500 dark:hover:border-blue-400">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide font-semibold">
                AI Screening Details
              </div>

              {/* Status Row - Inline. Derived from the applicant (with the explanation
                  metadata refining it once loaded), so it shows without waiting. */}
              <div className="mb-4 flex items-center gap-2">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Status
                </div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {status}
                </div>
              </div>

              {/* Timeline Details - Only show fields with dates */}
              <div className="flex gap-6">
                {/* Invited Section - Only show if has date */}
                {assessmentInvitedAt && (
                  <>
                    <div className="flex-1">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        Invited
                      </div>
                      <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                        {parseFormat(assessmentInvitedAt || "", "D MMMM YYYY")} at {parseFormat(assessmentInvitedAt || "", "h:mm A")}
                      </div>
                    </div>
                    {(assessmentStartedAt || assessmentCompletedAt) && (
                      <div className="w-px bg-gray-200 dark:bg-gray-700" />
                    )}
                  </>
                )}

                {/* Started Section - Only show if has date and not completed */}
                {assessmentStartedAt && status !== "Completed" && (
                  <>
                    <div className="flex-1">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        Started on
                      </div>
                      <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                        {parseFormat(assessmentStartedAt || "", "D MMMM YYYY")} at {parseFormat(assessmentStartedAt || "", "h:mm A")}
                      </div>
                    </div>
                    {assessmentCompletedAt && (
                      <div className="w-px bg-gray-200 dark:bg-gray-700" />
                    )}
                  </>
                )}

                {/* Completed Section - Only show if has date */}
                {status === "Completed" && assessmentCompletedAt && (
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      Completed
                    </div>
                    <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                      {parseFormat(assessmentCompletedAt || "", "D MMMM YYYY")} at {parseFormat(assessmentCompletedAt || "", "h:mm A")}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

              <div className="flex-1 overflow-y-auto py-4">
                {/* Collapsible Sections — each section renders immediately; only the
                    parts that consume employer/generate-score-explanation show a
                    local loader while that request is in flight, so sections that
                    don't need it (e.g. Eligibility Check) aren't blocked. */}
                <div className="space-y-4">
                  {/* CV Match Score Section — score comes from history/match_score
                      (immediate); the category breakdown items need the explanation
                      API, so its body shows a local loader until they arrive. */}
                  <ScoreSectionCollapsible
                    title="CV Score"
                    completedDate={applied_date ? parseFormat(applied_date, "D MMMM YYYY") : undefined}
                    completedTime={applied_date ? parseFormat(applied_date, "h:mm A") : undefined}
                      stage={"Applied"}
                      score={Math.round(cvScoreFromHistory ?? match_score)}
                    isLoading={isLoadingGenerateExplanation}
                    icon={<FileTextIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
                    items={breakdownItems.map((item) => ({
                      category: item.category,
                      score: item.score,
                      maxScore: item.max_score,
                      remarks: item.remarks,
                      mustHaves: item.mustHaves,
                    }))}
                    onShowMustHaves={(mustHaves) => {
                      setSelectedMustHaves(mustHaves);
                      setShowMustHaves(true);
                    }}
                  />

                  {/* Assessment Score Section — the section and its scores come from
                      score history, so they render immediately. Only the pieces that
                      genuinely need generate-score-explanation load in place: the AI
                      Interview breakdown rows (from interviewInsights) and the completed
                      date (which just appears once the metadata arrives). */}
                  {showAssessmentSection && (
                    <ScoreSectionCollapsible
                      title="AI Screening Score"
                      completedDate={scoreExplanationMetadata?.assessment_completed_at ? parseFormat(scoreExplanationMetadata.assessment_completed_at, "D MMMM YYYY") : undefined}
                      completedTime={scoreExplanationMetadata?.assessment_completed_at ? parseFormat(scoreExplanationMetadata.assessment_completed_at, "h:mm A") : undefined}
                        stage={"Screening in Progress"}
                        score={assessmentScore ?? 0}
                      maxScore={100}
                      icon={<ClipboardCheckIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
                      iconBgColor="bg-blue-100 dark:bg-blue-900/30"
                      items={[]}
                      defaultExpanded={true}
                      subSections={[
                        ...(hasAiAssessment ? [{
                          title: "AI Interview",
                          percentage: hasFormAssessment ? "70%" : "",
                          score: Math.round(assessmentScoreFromHistory ?? 0),
                          items: (interviewInsights?.interview_score || []).map((item) => ({
                            category: item.title,
                            score: item.score,
                            maxScore: item.max_score,
                            remarks: item.explanation,
                          })),
                          // The breakdown rows come from interviewInsights (fetched after
                          // generate-score-explanation) — load just this area in place.
                          isLoading: isLoadingGenerateExplanation || isLoadingInterviewInsight,
                        }] : []),
                        ...(hasFormAssessment ? [{
                          title: "Form",
                          percentage: hasAiAssessment ? "30%" : "",
                          // BE returns `score_percentage` already on a 0-100 scale. A null
                          // `score_percentage` means the form has no scoring at all —
                          // treat that as a full pass (100), not 0.
                          score: Math.round(formScoreFromHistory ?? (formDisplay?.score_percentage != null ? formDisplay.score_percentage : 100)),
                          maxScore: 100,
                          remarks: formDisplay?.explanation || "",
                        }] : []),
                      ]}
                    />
                  )}

                  {/* Eligibility Check Section — depends only on props (no explanation
                      API), so it renders immediately without waiting. */}
                  {applicant.elligibility_answers && (
                    <ScoreSectionCollapsible
                      title="Eligibility Check"
                      completedDate={scoreExplanationMetadata?.assessment_completed_at ? parseFormat(scoreExplanationMetadata.assessment_completed_at, "D MMMM YYYY") : undefined}
                      completedTime={scoreExplanationMetadata?.assessment_completed_at ? parseFormat(scoreExplanationMetadata.assessment_completed_at, "h:mm A") : undefined}
                        stage={"Screening in Progress"}
                      icon={
                        <AlertTriangleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      }
                      iconBgColor="bg-blue-100 dark:bg-blue-900/30"
                      items={(() => {
                        // job_data can be absent for some candidates (e.g. CV-less /
                        // partial records) — guard it so the score breakdown never crashes.
                        const meta = applicant.job_data?.work_authorization_metadata;
                        const answers = applicant.elligibility_answers;
                        if (!meta) return [];

                        const country = applicant.job_data?.location_metadata?.country || "";
                        const fieldConfig: { key: keyof typeof meta; category: string; getRemarks: (a: typeof answers) => string; getFlag?: (a: typeof answers) => { hasFlag: boolean; flagText?: string } }[] = [
                          {
                            key: "collect_employment_status",
                            category: "Current Employment",
                            getRemarks: (a) => {
                              if (!a?.employmentStatus) return "—";
                              const label = getEmploymentStatusLabel(a.employmentStatus);
                              return a.employmentStatusOther ? `${label} — ${a.employmentStatusOther}` : label;
                            },
                          },
                          {
                            key: "collect_expected_salary",
                            category: "Expected Salary",
                            getRemarks: (a) => a?.expectedSalary ? `${a.expectedSalaryCurrency || ""} ${Number(a.expectedSalary).toLocaleString()}/month`.trim() : "—",
                          },
                          {
                            key: "collect_start_availability",
                            category: "Earliest Start Date",
                            getRemarks: (a) => {
                              const option = availabilityOptions.find((item) => item.value === a?.startAvailability);
                              return option ? option.label : "—";
                            },
                          },
                          {
                            key: "collect_current_salary",
                            category: "Current or Last Drawn Salary",
                            getRemarks: (a) => a?.currentSalary ? `${a.currentSalaryCurrency || ""} ${Number(a.currentSalary).toLocaleString()}/month`.trim() : "—",
                          },
                          {
                            key: "collect_relocation_willingness",
                            category: "Willingness to Relocate",
                            getRemarks: (a) => a?.relocationWillingness === "yes" ? "Yes" : a?.relocationWillingness === "no" ? "No" : a?.relocationWillingness || "—",
                          },
                          {
                            key: "collect_weekend_shift_willingness",
                            category: "Availability for Overtime",
                            getRemarks: (a) => a?.shiftWillingness === "yes" ? "Yes" : a?.shiftWillingness === "no" ? "No" : a?.shiftWillingness || "—",
                          },
                          {
                            key: "collect_work_authorization",
                            category: "Work Authorization",
                            getRemarks: (a) => a?.workAuthorizationStatus ? getWorkAuthorizationLabel(a.workAuthorizationStatus, country) : "—",
                            getFlag: (a) => a?.workAuthorizationStatus === "needs_visa"
                              ? { hasFlag: true, flagText: `Requires work visa to work in ${country}` }
                              : { hasFlag: false },
                          },
                        ];

                        return fieldConfig
                          .filter(f => meta[f.key])
                          .map(f => {
                            const flag = f.getFlag?.(answers);
                            return {
                              category: f.category,
                              score: 0,
                              maxScore: 0,
                              remarks: f.getRemarks(answers),
                              hasFlag: flag?.hasFlag,
                              flagText: flag?.flagText,
                            };
                          });
                      })()}
                      isScored={false}
                    />
                  )}
                </div>

            </div>

        {/* Report Inaccuracy Section - sticky bottom */}
        {!isLoadingGenerateExplanation && (
          <div className="flex-shrink-0 pt-4 pb-2 px-4 border-t border-gray-200 dark:border-gray-700">
            {!showReportForm ? (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowReportForm(true)}
                  className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <AlertTriangleIcon className="w-4 h-4 mr-2" />
                  Report an issue
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    What issue would you like to report?
                  </label>
                  <Textarea
                    value={reportComment}
                    onChange={(e) => setReportComment(e.target.value)}
                    placeholder="Please describe the issue you've encountered with this score breakdown. Include specific details to help us investigate and improve our scoring system."
                    className="min-h-[100px] resize-none"
                    disabled={isSubmitting || submitSuccess}
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={handleCancelReport}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitReport}
                    disabled={!reportComment.trim() || isSubmitting}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Report"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        </div>

        {/* Right Panel - Must-Haves (Integrated) */}
        {showMustHaves && (
          <div className="w-[400px] flex flex-col border-l border-gray-200 dark:border-gray-700 flex-shrink-0 relative">
            {/* Header */}
            <div className="px-6 py-0 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Must-have Items
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    These must-have items are extracted from your job description.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  className="!absolute -top-2 right-0 hover:bg-transparant border-0 shadow-none"
                  onClick={() => setShowMustHaves(false)}
                >
                  <XIcon className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {selectedMustHaves.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No must-have items available.
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Matched items first */}
                  {selectedMustHaves
                    .filter((item) => item.matched)
                    .map((item, index) => (
                      <div key={`matched-${index}`} className="space-y-2">
                        <div className="flex items-start gap-3">
                          <div className="relative flex-shrink-0 mt-0.5">
                            <div className="w-5 h-5 rounded-full bg-green-500 dark:bg-green-600 flex items-center justify-center">
                              <svg
                                className="w-3 h-3 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          </div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-relaxed">
                            {item.label}
                          </div>
                        </div>
                        {item.explanation && (
                          <div className="flex items-start gap-2 pl-7">
                            <CornerDownRightIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                              {item.explanation}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}

                  {/* Unmatched items */}
                  {selectedMustHaves
                    .filter((item) => !item.matched)
                    .map((item, index) => (
                      <div key={`unmatched-${index}`} className="space-y-2">
                        <div className="flex items-start gap-3">
                          <div className="relative flex-shrink-0 mt-0.5">
                            <div className="w-5 h-5 rounded-full bg-red-500 dark:bg-red-600 flex items-center justify-center">
                              <svg
                                className="w-2.5 h-2.5 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </div>
                          </div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-relaxed">
                            {item.label}
                          </div>
                        </div>
                        {item.explanation && (
                          <div className="flex items-start gap-2 pl-7">
                            <CornerDownRightIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                              {item.explanation}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
